import { FC, useMemo, useState } from 'react'
import CN from 'classnames'
import {
  useCreateEmailAlertGroup,
  useGetPartnerAllList,
  useGetProcessOptions,
  useGetTagOptions,
} from 'framework/api/methods'

import { Button } from 'components/atoms/Button'
import { Drawer } from 'components/atoms/Drawer'
import { Input } from 'components/atoms/Input'
import { SimpleSelect } from 'components/atoms/Select'
import { isEmail } from 'utils'

export interface AddEmailAlertGroupProps {
  className?: string | undefined
  isActive: boolean
  onCloseClick: () => void
  notify: (message: string, type: 'success' | 'error') => void
  onCreated: () => void
}

interface PartnerOption {
  id: string
  value: string
  label: string
  code: string
}

const formatPartnerOptionLabel = (option: PartnerOption) => (
  <div className='flex items-center gap-2'>
    <span className='font-semibold text-N-800 truncate'>{option.label}</span>
    {option.code && (
      <span className='text-[11px] font-semibold text-N-500 bg-N-100 rounded px-2 py-[1px]'>
        {option.code}
      </span>
    )}
  </div>
)

export const AddEmailAlertGroup: FC<AddEmailAlertGroupProps> = ({
  className,
  isActive,
  onCloseClick,
  notify,
  onCreated,
}: AddEmailAlertGroupProps) => {
  const { data: partnerData, isLoading: partnersLoading } =
    useGetPartnerAllList()
  const { data: processOptionsData } = useGetProcessOptions()
  const { data: tagOptionsData } = useGetTagOptions()

  const partnerOptions = useMemo<PartnerOption[]>(() => {
    return (partnerData?.partners ?? [])
      .filter((partner: any) => partner.isActive !== false)
      .map((partner: any) => ({
        id: partner.id,
        value: partner.id,
        label: partner.name,
        code: partner.code ?? '',
      }))
  }, [partnerData])

  const processOptions: string[] = processOptionsData?.processOptions ?? []
  const tagOptions: string[] = tagOptionsData?.tagOptions ?? []

  const emptyForm = () => ({
    partner: null as PartnerOption | null,
    serviceName: '',
    serviceNameDescription: '',
    tag: '',
    recipientEmails: [''],
  })

  const [form, setForm] = useState(emptyForm())
  const [errors, setErrors] = useState<Record<string, string>>({})

  const { mutate: createGroup, isLoading: isCreating } =
    useCreateEmailAlertGroup()

  const resetAndClose = () => {
    setForm(emptyForm())
    setErrors({})
    onCloseClick()
  }

  const updateRecipientEmail = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.map((email, i) =>
        i === index ? value : email,
      ),
    }))
  }

  const addRecipientRow = () => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, ''],
    }))
  }

  const removeRecipientRow = (index: number) => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter((_, i) => i !== index),
    }))
  }

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}

    if (!form.partner) nextErrors.partner = 'Select a partner'
    if (!form.serviceName.trim())
      nextErrors.serviceName = 'Service name is required'
    if (!form.serviceNameDescription.trim())
      nextErrors.serviceNameDescription = 'Process/description is required'
    if (!form.tag.trim()) nextErrors.tag = 'Tag is required'

    const trimmedEmails = form.recipientEmails
      .map((email) => email.trim())
      .filter((email) => email.length > 0)

    if (trimmedEmails.length === 0) {
      nextErrors.recipientEmails = 'At least one recipient email is required'
    } else {
      const invalid = trimmedEmails.find((email) => !isEmail(email))
      if (invalid) {
        nextErrors.recipientEmails = `"${invalid}" is not a valid email address`
      } else {
        const lower = trimmedEmails.map((e) => e.toLowerCase())
        if (new Set(lower).size !== lower.length) {
          nextErrors.recipientEmails = 'Duplicate recipient emails entered'
        }
      }
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSave = () => {
    if (!validate()) return

    const recipientEmails = form.recipientEmails
      .map((email) => email.trim())
      .filter((email) => email.length > 0)

    createGroup(
      {
        partnerId: form.partner?.id,
        serviceName: form.serviceName.trim(),
        serviceNameDescription: form.serviceNameDescription.trim(),
        tag: form.tag.trim(),
        recipientEmails,
      },
      {
        onSuccess: () => {
          notify(
            `Created ${form.serviceName.trim()} for ${form.partner?.label}`,
            'success',
          )
          resetAndClose()
          onCreated()
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData?.message || 'Could not create email group', 'error')
        },
      },
    )
  }

  return (
    <Drawer
      isActive={isActive}
      onCloseClick={resetAndClose}
      width={440}
      title='New Email Group'>
      <div
        className={CN(
          'flex flex-col py-4 px-5 relative h-full overflow-y-auto overscroll-y-auto styled-scroll gap-y-5',
          className,
        )}>
        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Partner<span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <SimpleSelect
            isLoading={partnersLoading}
            placeholder='Select partner…'
            value={form.partner}
            onChange={(option: PartnerOption) =>
              setForm((prev) => ({ ...prev, partner: option }))
            }
            options={partnerOptions}
            formatOptionLabel={formatPartnerOptionLabel}
            isError={!!errors.partner}
          />
          {errors.partner && (
            <span className='text-R-500 text-xs pt-1'>{errors.partner}</span>
          )}
        </div>

        <div className='flex flex-col w-full'>
          <Input
            labelValue='Service Name'
            isRequired
            type='text'
            value={form.serviceName}
            onChange={(e: any) =>
              setForm((prev) => ({ ...prev, serviceName: e.target.value }))
            }
            isError={!!errors.serviceName}
          />
          {errors.serviceName && (
            <span className='text-R-500 text-xs pt-1'>
              {errors.serviceName}
            </span>
          )}
        </div>

        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Process / Service Description
            <span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <input
            list='email-alert-process-options'
            value={form.serviceNameDescription}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                serviceNameDescription: e.target.value,
              }))
            }
            className={CN(
              'w-full outline-none border border-N-200 rounded-md text-sm py-[9px] px-4',
              { 'border-R-200': !!errors.serviceNameDescription },
            )}
          />
          <datalist id='email-alert-process-options'>
            {processOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          {errors.serviceNameDescription && (
            <span className='text-R-500 text-xs pt-1'>
              {errors.serviceNameDescription}
            </span>
          )}
        </div>

        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Tag<span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <input
            list='email-alert-tag-options'
            placeholder='e.g. Inbound, Outbound'
            value={form.tag}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, tag: e.target.value }))
            }
            className={CN(
              'w-full outline-none border border-N-200 rounded-md text-sm py-[9px] px-4',
              { 'border-R-200': !!errors.tag },
            )}
          />
          <datalist id='email-alert-tag-options'>
            {tagOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          {errors.tag && (
            <span className='text-R-500 text-xs pt-1'>{errors.tag}</span>
          )}
        </div>

        <div className='flex flex-col w-full gap-2'>
          <span className='text-N-700'>
            Recipient email(s)
            <span className='text-R-500 ml-[2px]'>*</span>
          </span>
          {form.recipientEmails.map((email, index) => (
            <div key={index} className='flex items-center gap-2'>
              <input
                value={email}
                onChange={(e) => updateRecipientEmail(index, e.target.value)}
                placeholder='name@company.com'
                className='flex-1 px-3 py-[9px] border border-N-200 rounded-md text-sm outline-none text-N-800'
              />
              {form.recipientEmails.length > 1 && (
                <button
                  type='button'
                  onClick={() => removeRecipientRow(index)}
                  className='w-8 h-8 flex items-center justify-center bg-N-100 hover:bg-N-200 rounded-md text-N-600'>
                  <i className='ri-close-line text-[13px]'></i>
                </button>
              )}
            </div>
          ))}
          <button
            type='button'
            onClick={addRecipientRow}
            className='self-start text-P-600 text-[13px] font-semibold'>
            + Add another recipient
          </button>
          {errors.recipientEmails && (
            <span className='text-R-500 text-xs'>{errors.recipientEmails}</span>
          )}
        </div>
      </div>

      <div className='drawer-footer flex items-center w-full h-[67px] sticky bottom-0 px-5 border-t border-N-200 shadow-sm gap-x-5'>
        <Button
          isLoading={isCreating}
          disabled={isCreating}
          isFullWidth
          onClick={handleSave}>
          Save
        </Button>

        <Button isFullWidth appearance='dull' onClick={resetAndClose}>
          Close
        </Button>
      </div>
    </Drawer>
  )
}

AddEmailAlertGroup.defaultProps = {
  className: undefined,
}

export default AddEmailAlertGroup
